import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

import {
  AbsoluteBackgroundStack,
  type BackgroundConfig,
  BackgroundImage,
  BackgroundOverlay,
  resolveBackgroundConfig,
} from '@/components/shared/background-settings/absolute-stack';
import { ContentContainer } from '@/components/ui/content-container';
import { useDerivedContentTone } from '@/hooks/use-derived-content-tone';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { useEffectiveColors } from '@/hooks/use-public-design-settings';
import { SECTION_BACKGROUND_CONTENT_TONE_CLASSNAMES } from '@/lib/derive-content-tone';
import {
  DEFAULT_FOCUS_POINT,
  readBoolean,
  readNumber,
  resolveSectionFullWidth,
} from '@/lib/prop-utils';
import {
  parseSectionBackgroundInsetSize,
  parseSectionBackgroundSpacing,
  parseSectionBackgroundVerticalAlign,
  type SectionBackgroundInsetSize,
  type SectionBackgroundSpacing,
  type SectionBackgroundVerticalAlign,
} from '@/lib/section-background';
import { cn } from '@/lib/utils';
import { isPendingAIImage } from '@/types/image';
import { useSectionBackgroundLayout } from './use-section-background-layout';

export const resolveSectionBackgroundConfig = resolveBackgroundConfig;

function readStickyHeaderOffset(from: Element): number {
  const themeRoot =
    from.closest('[data-theme-root]') ?? from.ownerDocument.querySelector('[data-theme-root]');
  if (!themeRoot) {
    return 0;
  }

  for (const header of themeRoot.querySelectorAll('header')) {
    if (globalThis.getComputedStyle(header).position === 'sticky') {
      return header.getBoundingClientRect().height;
    }
  }

  return 0;
}

interface SectionBackgroundLayoutProps {
  config: BackgroundConfig;
  parallaxBackground: boolean;
  focusX: number;
  focusY: number;
}

const SECTION_SPACING_CLASSNAMES: Record<
  SectionBackgroundSpacing,
  Record<SectionBackgroundVerticalAlign, string>
> = {
  none: { top: '', center: '', bottom: '' },
  sm: { top: 'pb-4 md:pb-8', center: 'py-2 md:py-4', bottom: 'pt-4 md:pt-8' },
  md: {
    top: 'pb-20 md:pb-32',
    center: 'py-10 md:py-16',
    bottom: 'pt-20 md:pt-32',
  },
  lg: {
    top: 'pb-32 md:pb-48',
    center: 'py-16 md:py-24',
    bottom: 'pt-32 md:pt-48',
  },
};

const INSET_OUTER_CLASSNAMES: Record<SectionBackgroundInsetSize, string> = {
  sm: 'px-3 py-3 md:px-4 md:py-4',
  md: 'px-4 py-4 md:px-6 md:py-6',
  lg: 'py-6 md:py-10',
};

const INSET_INNER_CLASSNAMES: Record<SectionBackgroundInsetSize, string> = {
  sm: 'relative rounded-lg',
  md: 'relative rounded-lg',
  lg: 'relative mx-auto w-[88%] rounded-lg',
};

const insetInnerSurfaceClassName = (
  insetSize: SectionBackgroundInsetSize,
  parallaxBackground: boolean,
) =>
  cn(
    INSET_INNER_CLASSNAMES[insetSize],
    parallaxBackground ? '[clip-path:inset(0_round_0.5rem)]' : 'overflow-hidden',
  );

function SectionParallaxBackground({
  config,
  focusX,
  focusY,
  sectionHeight,
}: {
  config: BackgroundConfig;
  focusX: number;
  focusY: number;
  sectionHeight: number;
}) {
  const [stickyOffset, setStickyOffset] = useState(0);
  const stickyRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    setStickyOffset(readStickyHeaderOffset(node));
  }, []);

  if (!config.image) {
    return null;
  }

  return (
    <div
      ref={stickyRef}
      className="pointer-events-none sticky -z-10 h-0"
      style={{ top: stickyOffset }}
    >
      <div className="absolute left-0 w-full" style={{ top: 0, height: sectionHeight }}>
        <BackgroundImage image={config.image} focusX={focusX} focusY={focusY} />
        <BackgroundOverlay config={config} />
      </div>
    </div>
  );
}

function SectionBackgroundLayer({
  parallaxBackground,
  sectionHeight,
  config,
  focusX,
  focusY,
}: SectionBackgroundLayoutProps & {
  sectionHeight: number;
}) {
  if (parallaxBackground && sectionHeight > 0) {
    return (
      <SectionParallaxBackground
        config={config}
        focusX={focusX}
        focusY={focusY}
        sectionHeight={sectionHeight}
      />
    );
  }

  return <AbsoluteBackgroundStack config={config} focusX={focusX} focusY={focusY} />;
}

function SectionContent({
  config,
  editorMinHeightClass,
  children,
}: {
  config: BackgroundConfig;
  editorMinHeightClass: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex h-full flex-col',
        SECTION_SPACING_CLASSNAMES[config.spacing][config.verticalAlign],
        SECTION_BACKGROUND_CONTENT_TONE_CLASSNAMES[config.contentTone],
        editorMinHeightClass,
      )}
    >
      {children}
    </div>
  );
}

interface SectionSurfaceProps extends SectionBackgroundLayoutProps {
  surfaceRef?: ReturnType<typeof useSectionBackgroundLayout>['surfaceRef'];
  sectionHeight: number;
  editorMinHeightClass: string;
  children: ReactNode;
}

function FullWidthSectionSurface({
  config,
  parallaxBackground,
  surfaceRef,
  sectionHeight,
  focusX,
  focusY,
  fullWidth,
  editorMinHeightClass,
  children,
}: SectionSurfaceProps & { fullWidth: boolean }) {
  return (
    <div
      ref={surfaceRef}
      className={cn(
        'relative isolate',
        // clip-path clips sticky painting without breaking sticky like overflow-hidden can
        parallaxBackground ? '[clip-path:inset(0)]' : 'overflow-hidden',
      )}
    >
      <SectionBackgroundLayer
        parallaxBackground={parallaxBackground}
        sectionHeight={sectionHeight}
        config={config}
        focusX={focusX}
        focusY={focusY}
      />
      <ContentContainer as="div" passthrough={fullWidth} padding="none" className="relative z-10">
        <SectionContent config={config} editorMinHeightClass={editorMinHeightClass}>
          {children}
        </SectionContent>
      </ContentContainer>
    </div>
  );
}

function InsetSectionSurface({
  config,
  parallaxBackground,
  surfaceRef,
  sectionHeight,
  focusX,
  focusY,
  insetSize,
  editorMinHeightClass,
  children,
}: SectionSurfaceProps & { insetSize: SectionBackgroundInsetSize }) {
  return (
    <div className={INSET_OUTER_CLASSNAMES[insetSize]}>
      <div
        ref={surfaceRef}
        className={cn(
          'relative isolate',
          insetInnerSurfaceClassName(insetSize, parallaxBackground),
        )}
      >
        <SectionBackgroundLayer
          parallaxBackground={parallaxBackground}
          sectionHeight={sectionHeight}
          config={config}
          focusX={focusX}
          focusY={focusY}
        />
        <div className="relative z-10">
          <SectionContent config={config} editorMinHeightClass={editorMinHeightClass}>
            {children}
          </SectionContent>
        </div>
      </div>
    </div>
  );
}

interface BlockSectionSurfaceProps {
  sectionProps: Record<string, unknown>;
  children: ReactNode;
}

export function BlockSectionSurface({ sectionProps, children }: BlockSectionSurfaceProps) {
  const isPageEditor = useIsPageEditor();
  const editorMinHeightClass = isPageEditor ? 'min-h-20' : '';

  const siteTextColor = useEffectiveColors()?.siteText;
  const backgroundConfig = resolveBackgroundConfig(sectionProps, siteTextColor);
  const colorForTone = backgroundConfig?.mode === 'color' ? backgroundConfig.color : undefined;
  const derivedTone = useDerivedContentTone(colorForTone);

  const config =
    backgroundConfig && derivedTone
      ? { ...backgroundConfig, contentTone: derivedTone }
      : backgroundConfig;

  const fullWidth = resolveSectionFullWidth(sectionProps);
  const inset = readBoolean(sectionProps.backgroundInset) ?? false;
  const insetSize = parseSectionBackgroundInsetSize(sectionProps.backgroundInsetSize);

  const focusX = readNumber(sectionProps.backgroundFocusX) ?? DEFAULT_FOCUS_POINT;
  const focusY = readNumber(sectionProps.backgroundFocusY) ?? DEFAULT_FOCUS_POINT;
  const backgroundFixed = readBoolean(sectionProps.backgroundFixed) ?? true;

  const parallaxBackground =
    config?.mode === 'image' &&
    config.image != null &&
    !isPendingAIImage(config.image) &&
    backgroundFixed;

  const { surfaceRef, sectionHeight } = useSectionBackgroundLayout({
    enabled: parallaxBackground,
  });

  if (!config) {
    const spacing = parseSectionBackgroundSpacing(sectionProps.backgroundSpacing);
    const verticalAlign = parseSectionBackgroundVerticalAlign(sectionProps.backgroundVerticalAlign);

    return (
      <ContentContainer as="div" passthrough={fullWidth} padding="none">
        <div
          className={cn(
            'flex h-full flex-col',
            SECTION_SPACING_CLASSNAMES[spacing][verticalAlign],
            editorMinHeightClass,
          )}
        >
          {children}
        </div>
      </ContentContainer>
    );
  }

  const surfaceProps = {
    config,
    parallaxBackground,
    surfaceRef: parallaxBackground ? surfaceRef : undefined,
    sectionHeight,
    focusX,
    focusY,
    editorMinHeightClass,
    children,
  };

  if (!inset) {
    return <FullWidthSectionSurface {...surfaceProps} fullWidth={fullWidth} />;
  }

  return <InsetSectionSurface {...surfaceProps} insetSize={insetSize} />;
}
